import React, { useEffect, useRef, useState } from "react";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths2 from "oc-threejs/Maths2";
import * as Maths3 from "oc-threejs/Maths3";
import * as Utils from "../../commons/Utils";
import classnames from "classnames";
import { I18N } from "oc-I18N";
import Moveable from "react-moveable";
import { DragRulers } from "./DragRulers";
import { teilStyle } from "../../arrangement/grundrissFunctions";
import { Vector3 } from "three";
import * as _ from "lodash";
import { KeyState } from "oc-interaction/KeyState";
import { triggerChange } from "./GrundrissPage";
import { getKabelColor } from "./Stromkabellinie";
import { Aufbau } from "../../arrangement/Aufbau";
import { CoordinateSystem } from "./CoordinateSystem";

export enum Operation {
  DRAG = "Drag",
  RESIZE = "RESIZE",
  ROTATE = "ROTATE",
}

const { DRAG, RESIZE, ROTATE } = Operation;

export function MoveableTeil({
  cs,
  aufbau,
  insideOperation = null,
  modus,
  setInsideOperation,
  selected,
  setSelected,
  setAufbautenChange,
  editedKanteId,
}: {
  cs: CoordinateSystem;
  aufbau: Aufbau;
  insideOperation?: Operation | null;
  modus: string;
  setInsideOperation: (v: Operation | null) => void;
  selected: Aufbau | null | false;
  setSelected: (v: Aufbau | null) => void;
  setAufbautenChange: (v: Object) => void;
  editedKanteId?: number;
}) {
  const [, updateState] = useState<Object>();
  const [hover, setHover] = useState<boolean>();
  const [inputMode, setInputMode] = useState(false);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [snapGuidelines, setSnapGuidelines] = useState<SnapGuidelines>(null);
  const forceUpdate = () => {
    setAufbautenChange({});
    updateState({});
    triggerChange();
  };
  const { Key, position, size, drehung, aufbauMeta } = aufbau;
  const { Label } = aufbauMeta;
  const target = useRef();
  const onOperationStart = (operation) => () => {
    setSnapGuidelines(getSnapGuidlines(cs, aufbau));
    setInsideOperation(operation);
  };
  const onOperationEnd = () => {
    setInsideOperation(null);
    _.delay(() => APP.dirty(), 300);
  };
  const screenPosition = cs.pointToScreen(position);
  const screenSize = cs.sizeToScreen(size);
  const screenSizeOne = cs.sizeToScreen(v3(1, 0, 1));
  const halfSize = screenSize.clone().multiplyScalar(0.5);
  screenPosition.sub(halfSize);

  const translateToWorld = ([x, y]: number[]) => {
    return cs.pointToGrundriss(v2(x, y).add(halfSize));
  };

  const sizeTest = cs.sizeToGrundriss(screenSize as any as Vector3, size.y);

  const remove = () => {
    onOperationEnd();
    APP.arrangement.uiRemoveAufbauten([aufbau]);

    APP.arrangement.aufbauten = APP.arrangement.aufbauten.filter((a) => a !== aufbau);
    setAufbautenChange({});
    triggerChange();
    APP.dirty();
    APP.logUserAction({ action: "removeAufbau", aufbau });
  };

  const onEnterSize = (event) => {
    const text = event.target.value;
    const [x, z] = text
      .split("x")
      .map((s) => Number.parseFloat(s.replace(/[^0-9,.]/, "").replace(",", ".")));
    const { min } = aufbau.getMassMinMaxBox3();
    if (Number.isFinite(x)) {
      aufbau.size.setX(Math.max(x, min.x));
    }
    if (Number.isFinite(z)) {
      aufbau.size.setZ(Math.max(z, min.z));
    }

    setInputMode(false);
    setSelected(null);
    forceUpdate();
    APP.logUserAction({ action: "resizeAufbau", aufbau }, { cached: true });
  };

  useEffect(() => {
    const handleKey = (e) => {
      setIsCtrlPressed(KeyState.isCtrl() || KeyState.isShift());
      if (
        !inputMode &&
        editedKanteId == null &&
        (selected || hover) &&
        /[0-9,.+-]/.test(e.key + "")
      ) {
        setInputMode(true);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("keyup", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("keyup", handleKey);
    };
  }, [selected, hover, inputMode]);

  const formattedText = Utils.formatM(size.x, 2) + " x " + Utils.formatM(size.z, 2);

  const snapDistance = 10;
  const isTrafo = aufbau.isTrafo();

  const style = teilStyle(aufbauMeta);
  const aufbauStyle = isTrafo
    ? {
        ...style,
        backgroundColor: getKabelColor(aufbau),
      }
    : style;

  return (
    <React.Fragment>
      <div
        ref={target}
        className={classnames(
          "aufbau",
          selected == aufbau && "selected specificity",
          hover && "hovered"
        )}
        onClick={(event) => {
          if (insideOperation) {
            event.stopPropagation();
          }
          if (modus == "radieren") {
            remove();
          } else {
            setHover(false);
            setSelected(aufbau);
          }
        }}
        onDoubleClick={(event) => {
          if (insideOperation) {
            event.stopPropagation();
          }
          setHover(false);
          setSelected(aufbau);
          setInputMode(true);
        }}
        onMouseEnter={() => {
          if (modus !== "radieren" && !insideOperation && selected != aufbau) {
            setHover(true);
            setSelected(aufbau);
            setInputMode(false);
            APP.logUserAction({ action: "radierenAufbau", aufbau });
          }
        }}
        onMouseLeave={() => setHover(false)}
        style={{
          ...aufbauStyle,
          position: "absolute",
          width: screenSize.x + "px",
          height: screenSize.y + "px",
          backgroundSize: `${screenSizeOne.x}px ${screenSizeOne.y}px`,
          // darf nicht transform3d sein => sonst macht Moveable Mist
          transform: `translate(${screenPosition.x}px,${screenPosition.y}px) rotate(${
            (drehung * -180) / Math.PI
          }deg)`,
        }}
      >
        {Label}
        {selected == aufbau && (
          <div className="moveable-addon moveable-delete" onMouseDown={remove}>
            <div className="sign">✖</div>
          </div>
        )}
        {selected == aufbau && (insideOperation == null || insideOperation === RESIZE) && (
          <div
            className="moveable-addon moveable-size"
            onPointerDown={(e) => {
              if ((e.target as HTMLElement).classList.contains("moveable-size")) {
                e.stopPropagation();
                e.preventDefault();
                setSelected(aufbau);
                setInputMode(true);
              }
            }}
            onClick={(e) => {
              // notwendig, damit Click auf Größe => editMode funktioniert
              if ((e.target as HTMLElement).classList.contains("moveable-size")) {
                e.stopPropagation();
              }
            }}
            onMouseUp={(e) => {
              // notwendig, damit Click auf Größe => editMode funktioniert
              if ((e.target as HTMLElement).classList.contains("moveable-size")) {
                e.stopPropagation();
              }
            }}
            onPointerUp={(e) => {
              // notwendig, damit Click auf Größe => editMode funktioniert
              if ((e.target as HTMLElement).classList.contains("moveable-size")) {
                e.stopPropagation();
              }
            }}
          >
            {inputMode && !aufbau.fixedSize ? (
              <input
                type="text"
                defaultValue={formattedText}
                autoFocus={true}
                onBlur={onEnterSize}
                onKeyDown={(event) => {
                  if (event.key == "Enter") {
                    event.preventDefault();
                    onEnterSize(event);
                  }
                }}
                onClick={(e) => {
                  // notwendig, damit Click auf Größe => editMode funktioniert
                  e.stopPropagation();
                }}
                onMouseUp={(e) => {
                  // notwendig, damit Click auf Größe => editMode funktioniert
                  e.stopPropagation();
                }}
                onPointerUp={(e) => {
                  // notwendig, damit Click auf Größe => editMode funktioniert
                  e.stopPropagation();
                }}
              />
            ) : (
              formattedText
            )}
          </div>
        )}
        {selected == aufbau && insideOperation === ROTATE && (
          <div className="moveable-addon moveable-rotate">
            {I18N.formatFloat((aufbau.drehung * 180) / Math.PI, 0) + "°"}
          </div>
        )}
      </div>
      {selected == aufbau && (
        <Moveable
          target={target.current}
          draggable={true}
          resizable={aufbau.isResizeable()}
          rotatable={true}
          origin={false}
          snappable={!isCtrlPressed}
          snapThreshold={snapDistance}
          snapDirections={{
            left: true,
            top: true,
            right: true,
            bottom: true,
            center: true,
            middle: true,
          }}
          verticalGuidelines={snapGuidelines?.verticalGuidelines}
          horizontalGuidelines={snapGuidelines?.horizontalGuidelines}
          onDragStart={onOperationStart(DRAG)}
          onDragEnd={onOperationEnd}
          onRotateStart={onOperationStart(ROTATE)}
          onRotateEnd={onOperationEnd}
          onResizeStart={onOperationStart(RESIZE)}
          onResizeEnd={onOperationEnd}
          onDrag={({ translate }) => {
            aufbau.position.copy(translateToWorld(translate));
            forceUpdate();
          }}
          onRotate={({ absoluteRotation, drag }) => {
            aufbau.position.copy(translateToWorld(drag.translate));
            absoluteRotation = absoluteRotation % 360;
            if (absoluteRotation < 0) {
              absoluteRotation += 360;
            }
            aufbau.drehung = ((360 - absoluteRotation) * Math.PI) / 180;
            forceUpdate();
            APP.logUserAction({ action: "rotateAufbau", aufbau }, { cached: true });
          }}
          onResize={({ width, height, drag }) => {
            aufbau.position.copy(translateToWorld(drag.translate));
            const newScreenSize = v2(width, height);
            const newSize = cs.sizeToGrundriss(newScreenSize as any as Vector3, size.y);
            const minMaxBox = aufbau.getMassMinMaxBox3();
            const clampedSize = minMaxBox.clampPoint(newSize, v3());
            aufbau.size.copy(clampedSize);
            forceUpdate();
            APP.logUserAction({ action: "resizeAufbau", aufbau }, { cached: true });
          }}
        />
      )}
      {selected == aufbau && insideOperation !== null && insideOperation !== ROTATE && (
        <DragRulers aufbau={aufbau} cs={cs} />
      )}
    </React.Fragment>
  );
}

interface SnapGuidelines {
  horizontalGuidelines: number[];
  verticalGuidelines: number[];
}

function getSnapGuidlines(cs: CoordinateSystem, aufbau: Aufbau): SnapGuidelines {
  const agt = APP.arrangement;
  const nineteeDegree = Math.PI / 2;
  const linienSnapPunkte = agt.grundriss.kanten
    .filter((kante) => Maths2.isMultipleOfAngle(kante.direction.angle(), nineteeDegree))
    .flatMap((kante) => {
      const [a, b] = kante.ecken.map((p) => p.punkt);
      const scale = a.distanceTo(b);
      const pointAtScale = (s) =>
        Maths3.v2To3xz(a.clone().addScaledVector(kante.direction, s * scale));
      return [0, 0.5, 1].map(pointAtScale);
    });
  const aufbautenSnapPunkte = agt.aufbauten
    .filter((a) => a !== aufbau && Maths2.isMultipleOfAngle(a.drehung, nineteeDegree))
    .flatMap((a) => {
      const box = a.getBox().applyMatrix4(a.matrixWorld);
      const pointAtScale = (s) => box.grid(s, 0, s);
      return [0, 0.5, 1].map(pointAtScale);
    });

  const screenPoints = linienSnapPunkte.concat(aufbautenSnapPunkte).map((v) => cs.pointToScreen(v));
  const guidelines = {
    horizontalGuidelines: _.uniq(screenPoints.map((v) => v.y)) as number[],
    verticalGuidelines: _.uniq(screenPoints.map((v) => v.x)) as number[],
  };

  return guidelines;
}
